import React, { Component } from 'react'
import Head from 'next/head'
import styled, { createGlobalStyle } from 'styled-components'
import Layout from '../../components/Layout'
import Filter from '../../components/Filter/Filter'
import Text from '../../components/Text'
import Loader from '../../components/Loader'
import GoogleMap, { Marker, InfoBox } from '../../components/GoogleMap'
import { get } from '../../lib/fetch'
import MapLocation from '../../components/icons/MapLocation'
import Back from '../../components/icons/Back'
import SearchIcon from '../../components/icons/Search'
import RemoveIcon from '../../components/icons/Remove'
import { SCREEN_SIZE, visible, breakpoints, Media } from '../../lib/Media'
import getConfig from 'next/config'
import isEmpty from 'lodash/isEmpty'
import Router, { withRouter } from 'next/router'
import { generateQueryString, convertFilters } from '../../lib/filterFormatter'
import {
  disablePageScroll,
  calculateDistance,
  parseDistanceToText,
} from '../../lib/utils'
import MarkerContent from '../../components/MarkerContent'

const GlobalMainStyle = createGlobalStyle`
  main {
    padding: 0 !important;
  }
`

const {
  publicRuntimeConfig: { PUBLIC_API_URL },
} = getConfig()

const CENTER_ZOOM_SIZE = 14
const ADDRESSES_LIMIT_IN_SEARCH = 50
const SEARCH_ENTRY_DELAYING_TIME = 250
const DEFAULT_QUERY_STRING_PARAMS = {
  Latitude: 'latitude',
  Longitude: 'longitude',
  Filter: 'filter',
}

//TODO: We should provide an API call to get filters
const FILTERS = {
  Zone: [
    { label: 'North East', slug: 'North-East' },
    { label: 'North', slug: 'North' },
    { label: 'Central', slug: 'Central' },
    { label: 'West', slug: 'West' },
    { label: 'East', slug: 'East' },
  ],
  'Store Type': [
    { label: 'FairPrice', slug: 'FairPrice' },
    { label: 'FairPrice Finest', slug: 'FairPrice Finest' },
    { label: 'FairPrice Shop', slug: 'FairPrice Shop' },
    { label: 'FairPrice Xtra', slug: 'FairPrice Xtra' },
  ],
}

const Container = styled.div`
  display: flex;
  position: relative;
  height: calc(100vh - 10rem);

  ${SCREEN_SIZE.From.Tablet} {
    height: calc(100vh - 10rem);
  }
`

const FilterContainer = styled.div`
  display: flex;
  margin: 1.125rem 0rem 0;
  width: max-content;
  z-index: 2;
  position: absolute;
  right: 1rem;
  opacity: ${({ showPopup }) => (showPopup ? '0.2' : '1')};

  ${SCREEN_SIZE.From.Tablet} {
    right: 2rem;
  }

  ${SCREEN_SIZE.From.Desktop} {
    flex-direction: column;
    position: static;
    margin: 0rem;
    margin-right: 2rem;
    > div:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }
`

const MapContainer = styled.div`
  width: 100%;
  height: calc(100vh - 10rem);
  position: absolute;
  opacity: ${({ showPopup }) => (showPopup ? '0.2' : '1')};

  /* Customised InfoWindow style that could not handle by library */
  .gm-style-iw {
    padding: 0;
    max-width: inherit !important;
    max-height: inherit !important;

    .gm-style-iw-d {
      overflow: hidden !important;
      width: 18rem;
      max-width: inherit !important;
      max-height: inherit !important;
    }

    button.gm-ui-hover-effect {
      display: none !important;
    }
  }

  ${SCREEN_SIZE.From.Tablet} {
    position: static;
    height: calc(100vh - 10rem);
    top: -1.5rem;
    left: -2rem;
  }

  ${SCREEN_SIZE.From.Desktop} {
    position: static;
    margin-left: -2rem;
  }
`

const SearchInputContainer = styled.div`
  width: 80%;
  position: relative;
  box-shadow: 0 0.8125rem 0.9375rem 0 rgba(0, 0, 0, 0.12);
  border-radius: 1.5rem;
`

const StyledInput = styled.input`
  ${SCREEN_SIZE.Below.MobileL} {
    font-size: 1rem;
  }
  appearance: none;
  z-index: 1;
  left: 1rem;
  padding: 0.7rem 1rem 0.7rem 2.5rem;
  border-radius: 1.5rem;
  outline: none;
  border: none;
  box-shadow: 0 0.8125rem 0.9375rem 0 rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 2.25rem;
`

const SearchContent = styled.div`
  width: 100%;
  font-weight: bold;
`

const BottomSearch = styled.div`
  display: flex;
`

const DistanceContent = styled(Text)`
  margin-left: auto;
`

const SearchContainer = styled.div`
  position: absolute;
  z-index: 1;
  left: 1rem;
  margin-top: 1rem;
  display: flex;
  width: calc(100vw - 4.5rem);

  ${SCREEN_SIZE.From.Tablet} {
    left: 3rem;
    width: calc(100vw - 3rem);
  }

  ${SCREEN_SIZE.From.Desktop} {
    left: 20rem;
    width: 60%;
  }
`

const StyledSearchResults = styled.ul`
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 4px;

  ${SCREEN_SIZE.From.Tablet} {
    max-height: 23rem;
  }

  ${SCREEN_SIZE.From.Hd} {
    > a {
      display: inline-block;
      width: calc((100% - 1rem) / 2);
    }

    > a:nth-child(odd) {
      margin-right: 1rem;
    }
  }
`

export const LocationSearchResult = styled.div`
  position: absolute;
  margin-top: 1rem;
  z-index: 3;
  left: -4.5rem;
  height: calc(100vh - 4rem);
  padding: 1rem;
  background-color: #ffffff;
  border-top: 1px solid #eaeaea;
  width: 100%;
  box-shadow: 0 0.8125rem 0.9375rem 0 rgba(0, 0, 0, 0.12);

  ${StyledSearchResults} {
    max-height: calc(100vh - 5rem);
  }

  ${SCREEN_SIZE.From.Tablet} {
    height: auto;
    margin-top: 0;
    left: 0;
    top: calc(100% + 4px);
    border: 1px solid #eaeaea;
    border-radius: 4px;

    ${StyledSearchResults} {
      max-height: 23rem;
    }
  }
`

const SearchItem = styled.div`
  padding: 0.6rem 0.45rem;
  background-color: #ffffff;
  line-height: 1.25rem;
  font-size: 0.75rem;
  border-bottom: 1px solid #eaeaea;
  color: #696969;
  cursor: pointer;

  ${Text} {
    display: block;
    text-transform: capitalize;
    cursor: pointer;
  }
`

const StyledLocationIcon = styled(MapLocation)`
  width: 0.9375rem;
  height: 0.9375rem;
`

const CurrentLocationButton = styled.button`
  background: white;
  padding: 0.6rem;
  border-radius: 2rem;
  outline: none;
  border: 0;
  box-shadow: 0 13px 15px 0 rgba(0, 0, 0, 0.12);
  cursor: pointer;
  margin: 0 1rem;
  width: 2.25rem;
  height: 2.25rem;
`

const SearchPopover = styled.div`
  transform: unset !important;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  position: absolute;
  background: white;
  top: 0;
  left: 0;
  z-index: 10005;
  ${visible(SCREEN_SIZE.Only.Mobile)};
`

const SearchInputContainerInPopover = styled.div`
  width: 90%;
  position: relative;
  margin: auto;

  ${StyledInput} {
    box-shadow: none;
    border: 1px solid #eaeaea;
  }

  ${LocationSearchResult} {
    left: 0;
    padding: 0;
    box-shadow: none;
  }

  ${StyledSearchResults} {
    max-height: calc(100vh - 11rem);
    box-shadow: none;
  }
`

const BackIcon = styled(Back)`
  vertical-align: middle;
`

const SearchPopoverHeader = styled.div`
  border-bottom: 1px solid #eaeaea;
  padding: 1rem;
  margin-bottom: 1rem;

  ${Text} {
    width: 90%;
    display: inline-block;
    text-align: center;
    font-weight: bold;
  }
`

const ClearIcon = styled(RemoveIcon)`
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`

const StyledIcon = styled(SearchIcon)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  color: #1557bf;
`

class StoreLocator extends Component {
  static async getInitialProps() {
    const resp = await get(`${PUBLIC_API_URL}/stores`)
    const data = resp.data
    return { data }
  }

  constructor(props) {
    super(props)

    let filters = {}
    let currentLocation = undefined
    if (props.router?.query) {
      for (const key in props.router.query) {
        if (key === DEFAULT_QUERY_STRING_PARAMS.Filter) {
          filters = this.parseQueryStringToFilters(props.router.query[key])
        }
      }
      const lat = props.router.query[DEFAULT_QUERY_STRING_PARAMS.Latitude]
      const lng = props.router.query[DEFAULT_QUERY_STRING_PARAMS.Longitude]
      if (lat && lng) {
        currentLocation = {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        }
      }
    }

    this.state = {
      term: '',
      markers: [],
      filteredMarkers: [],
      sharedUserLocation: undefined,
      currentLocation: currentLocation,
      centerMarker: currentLocation,
      searchedAddresses: [],
      searchedStores: [],
      zoom: 12,
      isMarkerClicked: false,
      isSetCurrentLocation: !!currentLocation,
      filters: filters,
      showPopup: false,
    }

    this.handleMapMounted = this.handleMapMounted.bind(this)
    this.handleMarkerClick = this.handleMarkerClick.bind(this)
    this.handleResetLocation = this.handleResetLocation.bind(this)
    this.handleCurrentLocation = this.handleCurrentLocation.bind(this)
    this.search = this.search.bind(this)
    this.handleSearchChange = this.handleSearchChange.bind(this)
    this.handleApplyFilter = this.handleApplyFilter.bind(this)
    this.handlePinCurrentLocation = this.handlePinCurrentLocation.bind(this)
    this.setCurrentLocation = this.setCurrentLocation.bind(this)
    this.handleClearSearch = this.handleClearSearch.bind(this)
    this.closePopover = this.closePopover.bind(this)
    this.openPopover = this.openPopover.bind(this)
    this.handleAllowLocationAccess = this.handleAllowLocationAccess.bind(this)
    this.handleTouchStart = this.handleTouchStart.bind(this)
  }

  timer = undefined
  searchEntryRef = React.createRef()
  searchEntryInPopoverRef = React.createRef()

  handleMapMounted(map) {
    this.setState({ map: map }, () => {
      this.handleCurrentLocation()
    })
  }

  handleAllowLocationAccess() {
    if (!navigator || !navigator.geolocation) {
      return
    }

    const success = position => {
      this.setState({
        sharedUserLocation: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
      })
      this.setCurrentLocation(
        position.coords.latitude,
        position.coords.longitude
      )
    }

    const error = error => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          this.setState({ showPopup: false })
          break
      }
    }

    navigator.geolocation.getCurrentPosition(success, error, {
      enableHighAccuracy: true,
    })
  }

  changeRoute(filters, currentLocation) {
    let href = this.props.router.pathname
    const queryStringObject = {}

    if (currentLocation) {
      queryStringObject[DEFAULT_QUERY_STRING_PARAMS.Latitude] =
        currentLocation.lat
      queryStringObject[DEFAULT_QUERY_STRING_PARAMS.Longitude] =
        currentLocation.lng
    }

    if (filters && Object.keys(filters).length) {
      queryStringObject.filter = convertFilters(filters)
    }

    if (!isEmpty(queryStringObject)) {
      href += generateQueryString(queryStringObject)
    }

    if (href !== this.props.router.asPath) {
      Router.push(href)
    }
  }

  search(term) {
    this.setState({ loading: true }, async () => {
      try {
        const url = `${PUBLIC_API_URL}/address/search`

        let queryStringObject = {
          type: 'addresses',
          service: 'all',
          addressesLimit: ADDRESSES_LIMIT_IN_SEARCH,
          term: term,
        }

        if (this.state.currentLocation) {
          queryStringObject = {
            ...queryStringObject,
            lat: this.state.currentLocation.lat,
            lng: this.state.currentLocation.lng,
          }
        }

        const queryString = generateQueryString(queryStringObject)

        const resp = await get(url + queryString)
        const results = resp.data
        if (
          results &&
          Array.isArray(results.addresses) &&
          results.addresses.length
        ) {
          this.setState({
            searchedAddresses: results.addresses,
            searchedStores: results.fpstores,
            loading: false,
            error: '',
          })
        } else {
          this.setState({
            searchedAddresses: [],
            searchedStores: [],
            loading: false,
            error: 'No result found',
          })
        }
      } catch (error) {
        this.setState({ error: error.message, loading: false })
      }
    })
  }

  handleSearchChange(value) {
    if (value.startsWith(' ')) {
      value = value.trim()
    }
    // search only for search term of length 3 or more characters
    if (value && value.length < 3) {
      this.setState({ term: value, error: '' })
      return
    }
    if (this.timer) {
      clearTimeout(this.timer)
    }

    this.timer = setTimeout(() => {
      this.setState(
        { term: value, searchedAddresses: [], searchedStores: [], error: '' },
        () => {
          this.search(value)
        }
      )
    }, SEARCH_ENTRY_DELAYING_TIME)
  }

  setCenter(locations) {
    if (this.state.isMarkerClicked) {
      return {
        lat: this.state.activeStore.lat,
        lng: this.state.activeStore.long,
      }
    }

    if (this.state.isSetCurrentLocation) {
      return {
        lat: this.state.currentLocation.lat,
        lng: this.state.currentLocation.lng,
      }
    }

    if (locations.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds()
      for (let i = 0; i < locations.length; i++) {
        const location = {
          lat: locations[i].lat,
          lng: locations[i].long,
        }
        bounds.extend(location)
      }
      this.state.map && this.state.map.fitBounds(bounds)
      return bounds.getCenter()
    }
  }

  handleMarkerClick(marker, e) {
    this.setState({
      activeStore: marker,
      centerMarker: {
        lat: marker.lat,
        lng: marker.long,
      },
      zoom: CENTER_ZOOM_SIZE,
      isMarkerClicked: true,
    })
    if (e && e.target && e.target.nodeName === 'A') {
      window.open(e.target.href, '_blank')
    }
  }

  handleResetLocation() {
    this.setCenter(this.state.filteredMarkers)
  }

  setCurrentLocation(lat, lng, isClearedFilters = false) {
    if (!lat && !lng) {
      return
    }

    const currentLocation = {
      lat: lat,
      lng: lng,
    }
    let shortestDistance = undefined
    let activeStore = undefined
    for (const marker of this.state.filteredMarkers) {
      const calculatedDistance = calculateDistance(currentLocation, marker)
      if (
        shortestDistance === undefined ||
        shortestDistance >= calculatedDistance
      ) {
        shortestDistance = calculatedDistance
        activeStore = marker
      }
    }

    this.setState(
      {
        centerMarker: this.state.map ? this.state.map.getCenter() : undefined,
        zoom: this.state.map ? this.state.map.getZoom() : undefined,
      },
      () => {
        this.setState(
          prevState => {
            return {
              currentLocation,
              activeStore,
              showPopup: false,
              centerMarker: currentLocation,
              zoom: CENTER_ZOOM_SIZE,
              term: '',
              searchedAddresses: [],
              searchedStores: [],
              isSetCurrentLocation: true,
              isShowedSearchPopover: false,
              filters: isClearedFilters ? {} : prevState.filters,
            }
          },
          () => {
            this.clearSearchContent()
            this.changeRoute(this.state.filters, this.state.currentLocation)
          }
        )
      }
    )
  }

  clearSearchContent() {
    if (this.searchEntryRef.current) {
      this.searchEntryRef.current.value = ''
    }
    if (this.searchEntryInPopoverRef.current) {
      this.searchEntryInPopoverRef.current.value = ''
      this.searchEntryInPopoverRef.current.focus()
    }
  }

  handleClearSearch() {
    this.clearSearchContent()

    this.setState({
      term: '',
      searchedAddresses: [],
      searchedStores: [],
      loading: false,
      error: '',
    })
  }

  handleCurrentLocation(userLocation) {
    if (userLocation) {
      this.setCurrentLocation(userLocation.lat, userLocation.lng)
      return
    }

    if (this.state.currentLocation) {
      this.setCurrentLocation(
        this.state.currentLocation.lat,
        this.state.currentLocation.lng
      )
      return
    }

    this.handleAllowLocationAccess()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isShowedSearchPopover !== this.state.isShowedSearchPopover) {
      disablePageScroll(this.state.isShowedSearchPopover)
    }
  }

  componentDidMount() {
    const { data } = this.props
    const { fpstores } = data || {}

    const appliedFilteredMarkers = this.filter(this.state.filters, fpstores)

    this.setState(
      {
        markers: fpstores,
        filteredMarkers: appliedFilteredMarkers,
        isSetCurrentLocation: false,
        isMarkerClicked: false,
      },
      () => {
        !this.currentLocation && this.handleCurrentLocation()
      }
    )
  }

  parseQueryStringToFilters(data) {
    const result = {}
    if (data && typeof data === 'string') {
      const filterData = data.split('&')
      for (const filter of filterData) {
        const splittedData = filter.split(':')
        if (splittedData.length < 2) {
          return result
        }
        const filterKey = splittedData[0]
        const filterValues = splittedData[1]
        result[filterKey] = filterValues.split(',')
      }
    }
    return result
  }

  filter(filters, markers) {
    const cloneFilters = { ...filters }
    let appliedFilteredMarkers = markers ? [...markers] : []

    const zoneFilter = cloneFilters['Zone'] || []
    if (zoneFilter && zoneFilter.length > 0) {
      appliedFilteredMarkers = appliedFilteredMarkers.filter(marker => {
        for (const zone of zoneFilter) {
          if (marker.zoneId.includes(zone)) {
            return true
          }
        }
        return false
      })
    }

    const storeTypeFilter = cloneFilters['Store Type'] || []
    if (storeTypeFilter && storeTypeFilter.length > 0) {
      appliedFilteredMarkers = appliedFilteredMarkers.filter(marker =>
        storeTypeFilter.includes(marker.storeType)
      )
    }

    return appliedFilteredMarkers
  }

  handleApplyFilter(filters) {
    const appliedFilteredMarkers = this.filter(filters, this.state.markers)

    this.setState({
      filteredMarkers: appliedFilteredMarkers,
      activeStore: null,
      zoom: CENTER_ZOOM_SIZE,
      filters: filters,
      isMarkerClicked: false,
    })

    this.changeRoute(filters, this.state.currentLocation)
  }

  setMapStoreIcon(isActive) {
    return {
      url: '/static/map-store.png',
      scaledSize: isActive
        ? new window.google.maps.Size(50, 50)
        : new window.google.maps.Size(30, 30),
    }
  }

  handlePinCurrentLocation() {
    if (!this.state.sharedUserLocation) {
      this.handleAllowLocationAccess()
    }

    this.handleCurrentLocation(this.state.sharedUserLocation)
  }

  openPopover() {
    if (window.innerWidth >= breakpoints.tablet) {
      return
    }
    this.searchEntryRef.current.blur()
    this.setState({ isShowedSearchPopover: true, searchedAddresses: [] })
  }

  closePopover() {
    this.setState({
      isShowedSearchPopover: false,
      searchedAddresses: [],
      term: '',
    })
  }

  handleTouchStart() {
    //users start touching other areas in the screen, remove the keyboard of mobile
    this.searchEntryInPopoverRef &&
      this.searchEntryInPopoverRef.current &&
      this.searchEntryInPopoverRef.current.blur()
  }

  renderSearchedAddress = (address, index) => (
    <SearchItem
      key={`search-item-${index}`}
      onClick={() => {
        this.setCurrentLocation(address.lat, address.long, true)
      }}
    >
      {address.buildingName && (
        <SearchContent>
          <Text>{address.buildingName}</Text>
        </SearchContent>
      )}
      <BottomSearch>
        <Text>
          {`${address.buildingNumber}, ${address.streetName}, ${address.postcode}`}
        </Text>
        {!!address.distance && (
          <DistanceContent>
            {parseDistanceToText(parseFloat(address.distance))}
          </DistanceContent>
        )}
      </BottomSearch>
    </SearchItem>
  )

  render() {
    const { organizationData, categoryList } = this.props
    const { filteredMarkers, activeStore, showPopup } = this.state
    const { lat, lng } = this.state.currentLocation || {}

    let current = null
    if (lat && lng) {
      current = {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      }
    }

    return (
      <>
        <Head>
          <title>
            Store Locator | NTUC FairPrice | Your Nearest Grocery Store
          </title>
          <meta
            name="description"
            key="meta_description"
            content="Find a FairPrice store near you with locations and business hours."
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:title"
            content={
              'Store Locator | NTUC FairPrice | Find Your Nearest Grocery Store'
            }
          />
          <meta property="og:site_name" content="FairPrice" />
        </Head>
        <Layout
          categoryList={categoryList}
          organizationData={organizationData}
          noWrapper="fromDesktop"
        >
          <GlobalMainStyle />
          <React.Fragment>
            {this.state.isShowedSearchPopover && (
              <React.Fragment>
                <SearchPopover>
                  <SearchPopoverHeader>
                    <BackIcon
                      width="22"
                      height="22"
                      color="#1557bf"
                      onClick={() => {
                        this.closePopover()
                      }}
                    />
                    <Text>Store Locator</Text>
                  </SearchPopoverHeader>
                  <SearchInputContainerInPopover>
                    <StyledIcon title="Search" />
                    <StyledInput
                      placeholder="Search address..."
                      onChange={event =>
                        this.handleSearchChange(event.target.value)
                      }
                      autoFocus
                      ref={this.searchEntryInPopoverRef}
                    />
                    {this.state.term && (
                      <ClearIcon onClick={this.handleClearSearch} />
                    )}
                    {this.state.term !== '' && (
                      <LocationSearchResult>
                        <StyledSearchResults
                          onTouchStart={this.handleTouchStart}
                        >
                          {this.state.loading ? (
                            <Loader />
                          ) : this.state.term.length > 2 &&
                            Array.isArray(this.state.searchedAddresses) &&
                            this.state.searchedAddresses.length ? (
                            this.state.searchedAddresses.map(
                              this.renderSearchedAddress
                            )
                          ) : this.state.error ? (
                            <div
                              css={`
                                padding-top: 0.5rem;
                                overflow: hidden;
                              `}
                            >
                              {this.state.error}
                            </div>
                          ) : (
                            ''
                          )}
                        </StyledSearchResults>
                      </LocationSearchResult>
                    )}
                  </SearchInputContainerInPopover>
                </SearchPopover>
              </React.Fragment>
            )}
            <Container>
              <FilterContainer showPopup={showPopup}>
                <Filter
                  defaultValue={this.state.filters}
                  data={FILTERS}
                  variant="sitemap"
                  onApplyFilters={this.handleApplyFilter}
                />
              </FilterContainer>
              <MapContainer showPopup={showPopup}>
                <SearchContainer>
                  <SearchInputContainer>
                    <StyledIcon title="Search" />
                    <StyledInput
                      placeholder="Search address..."
                      onChange={event =>
                        this.handleSearchChange(event.target.value)
                      }
                      ref={this.searchEntryRef}
                      onFocus={() => {
                        this.openPopover()
                      }}
                    />
                    {this.state.term && (
                      <ClearIcon onClick={this.handleClearSearch} />
                    )}
                    <Media query={SCREEN_SIZE.From.Tablet}>
                      <React.Fragment>
                        {this.state.loading && this.state.term !== '' && (
                          <LocationSearchResult>
                            <StyledSearchResults>
                              <Loader />
                            </StyledSearchResults>
                          </LocationSearchResult>
                        )}
                        {!this.state.loading &&
                        this.state.term !== '' &&
                        Array.isArray(this.state.searchedAddresses) &&
                        this.state.searchedAddresses.length &&
                        this.state.term.length > 2 ? (
                          <LocationSearchResult>
                            <StyledSearchResults>
                              {this.state.searchedAddresses.map(
                                this.renderSearchedAddress
                              )}
                            </StyledSearchResults>
                          </LocationSearchResult>
                        ) : this.state.error ? (
                          <LocationSearchResult>
                            {this.state.error}
                          </LocationSearchResult>
                        ) : (
                          ''
                        )}
                      </React.Fragment>
                    </Media>
                  </SearchInputContainer>
                  <CurrentLocationButton
                    onClick={this.handlePinCurrentLocation}
                  >
                    <StyledLocationIcon color="#1557bf" />
                  </CurrentLocationButton>
                </SearchContainer>
                <GoogleMap
                  key={'google-map'}
                  currentLocation={current}
                  onClickCurrentLocation={this.handleCurrentLocation}
                  onMapMounted={this.handleMapMounted}
                  mapRef={this.state.map}
                  zoom={this.state.zoom}
                  center={this.state.centerMarker}
                  containerClassName="address-map"
                  setCenter={() => this.setCenter(filteredMarkers)}
                  defaultOptions={{
                    streetViewControl: false,
                    zoomControl: true,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    clickableIcons: false,
                    gestureHandling: 'greedy',
                  }}
                >
                  {this.state.map &&
                    filteredMarkers &&
                    filteredMarkers
                      .filter(
                        marker =>
                          marker?.address !== '' && marker?.postalCode !== '0'
                      )
                      .map((marker, index) => (
                        <Marker
                          defaultZIndex={3}
                          icon={this.setMapStoreIcon(
                            activeStore && activeStore.id === marker.id
                          )}
                          position={{ lat: marker.lat, lng: marker.long }}
                          key={'marker-' + index}
                          onClick={e => this.handleMarkerClick(marker, e)}
                          clickable
                          defaultClickable
                        >
                          {activeStore && activeStore.id === marker.id && (
                            <InfoBox
                              options={{
                                closeBoxURL: ``,
                                enableEventPropagation: true,
                                pixelOffset: new window.google.maps.Size(
                                  -140,
                                  0
                                ),
                              }}
                            >
                              <MarkerContent
                                ref={node => {
                                  this.markerRef = node
                                }}
                                currentLocation={this.state.currentLocation}
                                marker={activeStore}
                              />
                            </InfoBox>
                          )}
                        </Marker>
                      ))}
                </GoogleMap>
              </MapContainer>
            </Container>
          </React.Fragment>
        </Layout>
      </>
    )
  }
}

export default withRouter(StoreLocator)
